// Infotext zu einer Aufgabe beim Überfahren mit der Maus (Liste, Board, Gantt, Meine Aufgaben): eigenes Element statt
// des Browser-Titels, erscheint nach ~400 ms, folgt dem Mauszeiger nicht, bleibt im Fenster und verschwindet beim
// Verlassen, Scrollen, Klicken oder Ziehen. Nur für Mäuse – auf Touch-Geräten (pointerType ≠ mouse) nie.
// Der Inhalt kommt aus aufgabeInfo() in ../aufgaben.ts (reine Funktion, getestet).
import { useEffect, useLayoutEffect, useRef, useState, type PointerEvent as RPointerEvent } from 'react';
import { createPortal } from 'react-dom';
import { mdHtml } from '../logik/md.js';
import type { AufgabeInfoInhalt } from '../aufgaben';
import '../styles/aufgabe-info.css';

const VERZOEGERUNG_MS = 400;
const ABSTAND = 10;

interface Anzeige { inhalt: AufgabeInfoInhalt; x: number; unten: number; oben: number }

/**
 * Hook für eine Ansicht: `info(() => inhalt)` liefert die Ereignisse für das Element einer Aufgabe,
 * `element` wird einmal in die Ansicht gehängt (Portal an document.body).
 */
export function useAufgabeInfo() {
  const [anzeige, setAnzeige] = useState<Anzeige | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const maus = useRef({ x: 0, y: 0 });
  const gedrueckt = useRef(false);

  const ausblenden = () => setAnzeige((a) => (a ? null : a));
  const weg = () => {
    window.clearTimeout(timer.current);
    timer.current = undefined;
    ausblenden();
  };
  useEffect(() => {
    const bewegt = (e: PointerEvent) => { maus.current = { x: e.clientX, y: e.clientY }; };
    const runter = () => { gedrueckt.current = true; weg(); };
    const hoch = () => { gedrueckt.current = false; };
    window.addEventListener('pointermove', bewegt, { passive: true });
    window.addEventListener('pointerdown', runter, true);
    window.addEventListener('pointerup', hoch, true);
    window.addEventListener('pointercancel', hoch, true);
    // Scrollen blendet nur aus; ein noch wartender Infotext prüft beim Erscheinen selbst, ob die Maus noch auf der Aufgabe steht
    for (const t of ['scroll', 'wheel'] as const) window.addEventListener(t, ausblenden, { capture: true, passive: true });
    for (const t of ['dragstart', 'keydown', 'contextmenu'] as const) window.addEventListener(t, weg, true);
    return () => {
      window.clearTimeout(timer.current);
      window.removeEventListener('pointermove', bewegt);
      window.removeEventListener('pointerdown', runter, true);
      window.removeEventListener('pointerup', hoch, true);
      window.removeEventListener('pointercancel', hoch, true);
      for (const t of ['scroll', 'wheel'] as const) window.removeEventListener(t, ausblenden, true);
      for (const t of ['dragstart', 'keydown', 'contextmenu'] as const) window.removeEventListener(t, weg, true);
    };
  }, []);

  const info = (inhalt: () => AufgabeInfoInhalt) => ({
    onPointerEnter: (e: RPointerEvent<HTMLElement>) => {
      if (e.pointerType !== 'mouse' || e.buttons) return;
      const el = e.currentTarget;
      maus.current = { x: e.clientX, y: e.clientY };
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => {
        timer.current = undefined;
        const { x, y } = maus.current;
        if (gedrueckt.current || !el.isConnected || !el.contains(document.elementFromPoint(x, y))) return;
        const r = el.getBoundingClientRect();
        // waagrecht an der Mausposition beim Erscheinen, senkrecht unter (oder über) dem Element – folgt danach nicht mehr
        setAnzeige({ inhalt: inhalt(), x: Math.min(Math.max(x, r.left), r.right), unten: r.bottom, oben: r.top });
      }, VERZOEGERUNG_MS);
    },
    onPointerLeave: weg,
  });

  const element = anzeige ? createPortal(<InfoBox a={anzeige} />, document.body) : null;
  return { info, element };
}

/** Kasten mit dem Infotext; nach dem ersten Zeichnen ins Fenster gerückt (unter dem Element, sonst darüber). */
function InfoBox({ a }: { a: Anzeige }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const w = el.offsetWidth, h = el.offsetHeight, vw = window.innerWidth, vh = window.innerHeight;
    const left = Math.max(8, Math.min(a.x - 16, vw - w - 8));
    let top = a.unten + ABSTAND;
    if (top + h > vh - 8) top = a.oben - ABSTAND - h >= 8 ? a.oben - ABSTAND - h : Math.max(8, vh - h - 8);
    setPos({ left, top });
  }, [a]);
  const { inhalt } = a;
  return (
    <div ref={ref} className="aufgabe-info" role="tooltip" data-testid="aufgabe-info"
      style={pos ? { left: pos.left, top: pos.top } : { left: 0, top: 0, visibility: 'hidden' }}>
      <div className="titel">{inhalt.titel}</div>
      <dl>
        {inhalt.zeilen.map((z) => <div key={z.name} className={z.warn ? 'warn' : undefined}><dt>{z.name}</dt><dd>{z.wert}</dd></div>)}
      </dl>
      {inhalt.beschreibung && (
        <div className="beschreibung">
          <div className="md" dangerouslySetInnerHTML={{ __html: mdHtml(inhalt.beschreibung) }} />
          {inhalt.gekuerzt && <div className="hint">(gekürzt – vollständig im Detail)</div>}
        </div>
      )}
    </div>
  );
}
